import { deviceDatabaseOwners, openDeviceDatabaseExclusive, type StoredView } from '../outbox/device-database';

/**
 * 036 (issue 208, FR-045, FR-048) — the `views` object store's own read/write/clear, scoped to one
 * practitioner's device database (`core/outbox/device-database.ts` owns the schema; this file owns
 * nothing but how `core/stored-views/` reaches it).
 *
 * **A connection is opened, used once, and closed — never cached across calls.** The outbox keeps a
 * long-lived handle per practitioner because it has a send loop and a mirror to keep live; a stored
 * view is read or written at most a handful of times per screen load, so the simpler, harder-to-get-
 * wrong shape is to pay one `openDB` round trip per call rather than reason about a second cached
 * handle's `closed` flag alongside the outbox's own (`core/outbox/outbox.store.ts`'s own comment on
 * why THAT cache exists is a reason peculiar to a send loop, not a general argument for one here).
 *
 * **`openDeviceDatabaseExclusive`, never the shared `openDeviceDatabase`.** The shared entry point
 * de-duplicates concurrent opens by handing the SAME connection to every caller racing for the same
 * name — correct between two long-lived callers who both mean to keep it, and wrong here: this file
 * closes what it gets back the instant its own one operation finishes, and closing a connection the
 * outbox's own long-lived handle was also handed closes the outbox out from under itself.
 * `device-database.ts`'s own comment on the exclusive opener has the regression this fixed.
 */

/**
 * 036 (issue 258, B2) — `stillValid`, when given, is checked IMMEDIATELY before `db.putView(view)` —
 * not earlier, and with no `await` between the check and that call. `openDeviceDatabaseExclusive`
 * above is itself a real round trip (opening a fresh connection, never cached — this file's own
 * header says why), which is exactly the gap a check made any earlier (`storing-client.ts`'s own
 * epoch comparison, made before this function is even called) cannot see across: `clear()`'s
 * `clearStoredViewsFor` can bump the epoch and have its OWN `db.clear('views')` call create, run and
 * finish its transaction while THIS call is still waiting on its connection to open. `idb`'s `put`
 * creates the native `IDBTransaction` synchronously, inside the same call that issues the request —
 * so `stillValid()` immediately before it, with nothing awaited in between, runs in the exact same
 * synchronous turn as that transaction's creation. JavaScript is single-threaded and run-to-completion
 * within a turn, so nothing — not even a sign-out's own synchronous `bumpEpoch()` — can land between
 * this check and this write's own transaction being created once the check has passed: the two are no
 * longer two events that can be reordered, only one. A caller that checks earlier (or not at all) is
 * unaffected — the parameter is optional and changes nothing about what is written when it is true.
 */
export async function writeStoredView(
  practitionerId: number | string,
  view: StoredView,
  stillValid?: () => boolean,
): Promise<void> {
  const db = await openDeviceDatabaseExclusive(practitionerId);
  try {
    if (stillValid && !stillValid()) return;
    await db.putView(view);
  } finally {
    db.close();
  }
}

export async function readStoredView(practitionerId: number | string, url: string): Promise<StoredView | null> {
  const db = await openDeviceDatabaseExclusive(practitionerId);
  try {
    return await db.getView(url);
  } finally {
    db.close();
  }
}

/** FR-046 — every stored view gone, on a sign-out or a refusal of the session. Leaves the outbox's
 *  own rows untouched (FR-050): `DeviceDatabase.clearViews()` only ever clears `views`. */
export async function clearStoredViewsFor(practitionerId: number | string): Promise<void> {
  const db = await openDeviceDatabaseExclusive(practitionerId);
  try {
    await db.clearViews();
  } finally {
    db.close();
  }
}

/**
 * Issue 280 — FR-046 for a refusal that arrives with NO account in memory (a cold launch whose
 * stored token had no identity pairing with it). There is nobody to name, so the device's own
 * databases are the record of whose views are here, and every one of them is cleared.
 *
 * Clearing all of them is safe, not merely thorough: a browser profile holds one session, and every
 * sign-out already clears its own practitioner's views, so at the moment of a refusal there is no
 * other practitioner whose stored views are legitimately on this device. The outbox's rows are left
 * exactly as `clearStoredViewsFor` leaves them (FR-050).
 *
 * One practitioner's database failing to clear does not stop the others.
 */
export async function clearEveryStoredView(): Promise<void> {
  const outcomes = await Promise.allSettled((await deviceDatabaseOwners()).map((owner) => clearStoredViewsFor(owner)));
  const failed = outcomes.find((outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected');
  if (failed) throw failed.reason;
}
